import { createSelector } from 'reselect';
import { isEmpty } from 'underscore';
import { ClipSuggestion } from 'blocks/ClipSelect/types';
import {
  GroupClipSuggestion,
  SubscriptionItemInformation,
} from 'redux/middleware/api/podcast-service/types';
import {
  groupClipSuggestionSelector,
  groupClipSuggestionsSelector,
  podcastSubscriptionsSelector,
  subscriptionItemInformationSelector,
} from 'redux/modules/entities';
import { State } from 'redux/types';
import { getValue } from 'utils/collections';

const subscriptionItemIdSelector = (_, subscriptionItemId: number) =>
  subscriptionItemId;

const subscriptionItemSelector = createSelector(
  subscriptionItemIdSelector,
  subscriptionItemInformationSelector,
  (subscriptionItemId, info) =>
    info
      ?.get(String(subscriptionItemId))
      ?.toJS() as SubscriptionItemInformation,
);

export const hasSubscriptionItemInformationSelector = createSelector(
  subscriptionItemSelector,
  item => !isEmpty(item),
);

export const autoCreateSubscriptionIdSelector = createSelector(
  subscriptionItemSelector,
  item => item?.autoCreateSubId,
);

export const podcastSubscriptionSelector = createSelector(
  autoCreateSubscriptionIdSelector,
  podcastSubscriptionsSelector,
  (autoCreateSubscriptionId, subscriptions) => {
    if (!autoCreateSubscriptionId || !subscriptions) {
      return undefined;
    }

    return getValue(subscriptions, String(autoCreateSubscriptionId))?.toJS();
  },
);

function createClipSuggestion(
  gs: GroupClipSuggestion,
): ClipSuggestion | undefined {
  if (!gs) {
    return undefined;
  }

  const { autoCreateEpisodeId, transcriptSegments, text, ...rest } = gs;

  return {
    ...rest,
    id: autoCreateEpisodeId,
    text: isEmpty(transcriptSegments) ? text : transcriptSegments,
  } as ClipSuggestion;
}

export const suggestedClipsSelector = createSelector(
  groupClipSuggestionsSelector,
  (suggestions): ClipSuggestion[] =>
    suggestions?.get('clipSuggestions')?.reduce((acc, s) => {
      if (s.get('status') !== 'failed') {
        acc.push(createClipSuggestion(s.toJS()));
      }
      return acc;
    }, []) ?? [],
);

export const suggestionSelector = (state: State, suggestionId: number) => {
  const suggestion = groupClipSuggestionSelector(suggestionId, state);

  if (!suggestion) {
    return undefined;
  }

  return createClipSuggestion(suggestion);
};
